<template>
  <div
    ref="sliderRef"
    class="b-swiper preview-cases-slider"
    data-qa="preview-cases-slider"
  >
    <div
      v-once
      class="b-swiper__track"
      data-qa="preview-cases-slider_track"
    >
      <div
        v-for="(doctorCase, index) in doctorCases"
        ref="slidesRef"
        :key="index"
        data-qa="preview-cases-slider_slide"
        class="b-swiper__slide preview-cases-slider__slide"
        @click="emitClickSlide(doctorCase)"
      >
        <div
          class="preview-cases-slider__image-preview-wrapper mb-4"
        >
          <img
            v-for="i in Math.min(doctorCase.count_images, 3)"
            :key="i"
            :src="doctorCase.photo_preview_path"
            :alt="`${$t('Фото до и после', { ns: $i18next.language })} - ${index + 1}`"
            width="140"
            height="140"
            data-qa="preview-cases-slider_slide_image"
            class="preview-cases-slider__image-preview"
            aria-grabbed="false"
            draggable="false"
          >
        </div>
        <span
          class="preview-cases-slider__price text-subtitle-2"
          data-qa="preview-cases-slider_slide_price"
        >
          {{ getFormattedPrice(doctorCase.total_price) }}
        </span>
      </div>

      <div
        v-if="doctorCaseCount > 10"
        ref="slideMoreRef"
        class="b-swiper__slide preview-cases-slider__slide-more"
        data-qa="preview-cases-slider_slide_more"
      >
        <v-btn
          color="primary"
          class="px-2"
          text
          depressed
          @click="emitClickSlide"
        >
          <span class="text-button mr-2">
            {{ $t('Смотреть все', { ns: $i18next.language }) }}
          </span>
          <span class="ui-icon-arrow-right" />
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { getFormattedPrice } from 'www/utils'
import SwiperModule from 'www/modules/SwiperModule'
import type { DoctorCase } from 'www/vue_components/common/DoctorCases/types'

defineProps<{
  doctorCases: DoctorCase[]
  doctorCaseCount: number
}>()

type Emits = {
  (_e: 'click-slide', _doctorCase?: DoctorCase): void
}

const emit = defineEmits<Emits>()

const isMobile = window.MOBILE_VERSION

const sliderRef = ref()
const slidesRef = ref()
const slideMoreRef = ref()

onMounted(() => {
  if (!sliderRef.value || !slidesRef.value) {
    return
  }

  // Свайпер не нужен, если все слайды помещаются
  const isSwiperNeed = isMobile
    ? slidesRef.value!.length > 1
    : slidesRef.value!.length > 3

  if (isSwiperNeed) {
    new SwiperModule({
      module: {
        modifier: 'preview-cases-slider',
      },
      swiper: {
        pagination: false,
        slidesPerView: 'auto',
        spaceBetween: 0,
        centeredSlides: false,
        roundLengths: false,
      },
    })
  }
})

function emitClickSlide(doctorCase?: DoctorCase) {
  emit('click-slide', doctorCase)
}
</script>

<style lang="scss">
@import '~www/themes/doctors/common/variables';

.preview-cases-slider {
  width: 100%;

  &__slide {
    box-sizing: border-box;
    padding: 16px;
    width: 202px;
    display: flex;
    align-items: center;
    flex-direction: column;
    border-radius: $border-radius-lg;
    border: 1px solid $ui-kit-bg-gray-60;
    cursor: pointer;

    &:not(:last-child) {
      margin-right: 8px;
    }

    &-more {
      padding: 0;
      flex-direction: row;
      justify-content: end;
      margin-top: auto;
      margin-bottom: auto;
      border: none;
      width: 176px;
    }
  }

  &__image-preview-wrapper {
    position: relative;
    width: 170px;
    height: 170px;
  }

  &__image-preview {
    position: absolute;
    display: block;
    inset: 15px;
    border-radius: $border-radius-md;

    // Аналогично elevation-1
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.12),
      0 1px 1px 0 rgba(0, 0, 0, 0.14),
      0 1px 3px -2px rgba(0, 0, 0, 0.2) !important;

    &:nth-child(2) {
      transform: rotate(-10deg);
      z-index: -1;
    }

    &:nth-child(3) {
      transform: rotate(10deg);
      z-index: -2;
    }
  }

  &__price {
    display: inline-flex;
    align-items: center;
    padding: 0 12px;
    height: 32px;
    background-color: $ui-kit-bg-primary;
    color: $ui-kit-primary;
    border-radius: 9999px;
  }

  &__btn-more {
    color: $ui-kit-primary;
    transition-property: background-color;
    transition-duration: 200ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      background-color: $ui-kit-bg-primary;
    }
  }
}
</style>
